import * as React from 'react'
import Box from '@mui/material/Box'
import { HOUR_PX } from './weekGeometry'
import { VISIBLE_HOURS, initialScrollTop } from './scrollPosition'

// Room for the sticky day header on top of the visible hours.
const HEADER_ALLOWANCE_PX = 48
const VIEWPORT_HEIGHT = VISIBLE_HOURS * HOUR_PX + HEADER_ALLOWANCE_PX

type Props = {
  /** The days shown; a different first day means a new week or day. */
  days: Date[]
  /** Names the region, e.g. "Week view". */
  label: string
  children: React.ReactNode
}

/**
 * The scrolling window onto the 24-hour body. The header sticks to its top so
 * its columns stay aligned with the body's. Scrolls to its starting position
 * on mount and whenever the user moves to another week or day, not on every
 * re-render, so a refetch never throws away where the user scrolled to.
 */
export function TimeScroller({ days, label, children }: Props) {
  const ref = React.useRef<HTMLDivElement>(null)
  const daysRef = React.useRef(days)
  daysRef.current = days
  const resetKey = days[0].getTime()

  React.useLayoutEffect(() => {
    if (ref.current) {
      ref.current.scrollTop = initialScrollTop(daysRef.current, new Date())
    }
  }, [resetKey])

  return (
    // Focusable so keyboard users can scroll it. `region`, not `grid`: the
    // grid pattern needs rows/cells and arrow keys this read-only view lacks.
    <Box
      ref={ref}
      data-test-id="time-scroller"
      role="region"
      aria-label={label}
      tabIndex={0}
      sx={{
        maxHeight: VIEWPORT_HEIGHT,
        overflowY: 'auto',
        position: 'relative',
        '&:focus-visible': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: -2,
        },
      }}
    >
      {children}
    </Box>
  )
}
